import { createLogger } from '../utils/logger';
import { supabase } from '../utils/supabase';
import { UserNotification } from '../utils/notificationService';

const logger = createLogger({ module: 'PollingNotificationService', level: 'info' });

interface NotificationListener {
  id: string;
  callback: (notification: UserNotification & { _deleted?: boolean }) => void;
}

interface PollingState {
  intervalId: NodeJS.Timeout;
  lastCheckTimestamp: string;
  consecutiveErrors: number;
}

class PollingNotificationService {
  private static instance: PollingNotificationService | null = null;
  private pollingStates: Map<string, PollingState> = new Map();
  private listeners: Map<string, NotificationListener[]> = new Map();
  private isDestroyed = false;
  private readonly POLLING_INTERVAL = 30000; // 30 seconds

  private constructor() {
    logger.info('PollingNotificationService instance created');
  }

  static getInstance(): PollingNotificationService {
    if (!PollingNotificationService.instance) {
      PollingNotificationService.instance = new PollingNotificationService();
    }
    return PollingNotificationService.instance;
  }

  static async destroyInstance(): Promise<void> {
    if (PollingNotificationService.instance) {
      await PollingNotificationService.instance.destroy();
      PollingNotificationService.instance = null;
    }
  }

  async subscribe(userId: string): Promise<void> {
    if (this.isDestroyed) {
      logger.warn('Cannot subscribe - service is destroyed');
      return;
    }

    // Check if already polling for this user
    if (this.pollingStates.has(userId)) {
      logger.debug('Already polling for user', { userId });
      return;
    }

    logger.info('Starting polling for user', { userId });

    // Initialize last check timestamp to now
    const lastCheckTimestamp = new Date().toISOString();

    // Start polling interval
    const intervalId = setInterval(() => {
      this.pollNotifications(userId);
    }, this.POLLING_INTERVAL);

    // Store polling state
    this.pollingStates.set(userId, {
      intervalId,
      lastCheckTimestamp,
      consecutiveErrors: 0
    });

    // Do an immediate poll to catch any existing notifications
    await this.pollNotifications(userId);
  }

  async unsubscribe(userId?: string): Promise<void> {
    if (userId) {
      // Unsubscribe specific user
      const state = this.pollingStates.get(userId);
      if (state) {
        clearInterval(state.intervalId);
        this.pollingStates.delete(userId);
        this.listeners.delete(userId);
        logger.info('Stopped polling for user', { userId });
      }
    } else {
      // Unsubscribe all users
      for (const state of this.pollingStates.values()) {
        clearInterval(state.intervalId);
      }
      this.pollingStates.clear();
      this.listeners.clear();
      logger.info('Stopped polling for all users');
    }
  }

  addListener(userId: string, callback: (notification: UserNotification & { _deleted?: boolean }) => void): () => void {
    if (this.isDestroyed) {
      logger.warn('Cannot add listener - service is destroyed');
      return () => {};
    }

    const listenerId = `listener_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    const listener: NotificationListener = { id: listenerId, callback };

    if (!this.listeners.has(userId)) {
      this.listeners.set(userId, []);
    }
    this.listeners.get(userId)!.push(listener);

    logger.debug('Added listener for user', { userId, listenerId });

    // Return cleanup function
    return () => {
      const userListeners = this.listeners.get(userId);
      if (userListeners) {
        const index = userListeners.findIndex(l => l.id === listenerId);
        if (index !== -1) {
          userListeners.splice(index, 1);
          if (userListeners.length === 0) {
            this.listeners.delete(userId);
          }
          logger.debug('Removed listener for user', { userId, listenerId });
        }
      }
    };
  }

  private async pollNotifications(userId: string): Promise<void> {
    const state = this.pollingStates.get(userId);
    if (!state) {
      logger.warn('No polling state found for user', { userId });
      return;
    }

    try {
      const currentTimestamp = new Date().toISOString();

      // Query for new notifications since last check with limit
      const { data: newNotifications, error: newError } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .gt('created_at', state.lastCheckTimestamp)
        .order('created_at', { ascending: true })
        .limit(100);

      if (newError) {
        logger.error('Failed to fetch new notifications', newError, { userId });
        state.consecutiveErrors++;
        
        // Alert after 3 consecutive failures
        if (state.consecutiveErrors >= 3) {
          logger.error('Multiple consecutive polling failures', { 
            userId, 
            consecutiveErrors: state.consecutiveErrors 
          });
        }
        return;
      }

      // Query for dismissed notifications since last check with limit
      const { data: deletedNotifications, error: deleteError } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .gt('dismissed_at', state.lastCheckTimestamp)
        .not('dismissed_at', 'is', null)
        .order('dismissed_at', { ascending: true })
        .limit(100);

      if (deleteError) {
        logger.error('Failed to fetch dismissed notifications', deleteError, { userId });
        state.consecutiveErrors++;
        
        // Alert after 3 consecutive failures
        if (state.consecutiveErrors >= 3) {
          logger.error('Multiple consecutive polling failures', { 
            userId, 
            consecutiveErrors: state.consecutiveErrors 
          });
        }
        return;
      }

      // Reset consecutive errors on successful fetch
      state.consecutiveErrors = 0;

      // Notify listeners of new notifications
      const userListeners = this.listeners.get(userId);
      if (userListeners && userListeners.length > 0) {
        // Process new notifications
        if (newNotifications && newNotifications.length > 0) {
          logger.debug('Found new notifications', { userId, count: newNotifications.length });
          for (const notification of newNotifications) {
            for (const listener of userListeners) {
              try {
                listener.callback(notification as UserNotification);
              } catch (error) {
                logger.error('Error in notification listener', error);
              }
            }
          }

          // Mark notifications as delivered
          const notificationIds = newNotifications.map(n => n.id);
          try {
            await supabase
              .from('notifications')
              .update({ delivered_at: new Date().toISOString() })
              .in('id', notificationIds)
              .is('delivered_at', null);
          } catch (error) {
            logger.error('Failed to mark notifications as delivered', error);
          }
        }

        // Process dismissed notifications
        if (deletedNotifications && deletedNotifications.length > 0) {
          logger.debug('Found dismissed notifications', { userId, count: deletedNotifications.length });
          for (const notification of deletedNotifications) {
            for (const listener of userListeners) {
              try {
                listener.callback({
                  ...(notification as UserNotification),
                  _deleted: true
                });
              } catch (error) {
                logger.error('Error in notification listener', error);
              }
            }
          }
        }
      }

      // Update last check timestamp ONLY after successful processing
      state.lastCheckTimestamp = currentTimestamp;
    } catch (error) {
      logger.error('Error polling notifications', error, { userId });
      // Don't throw - next poll will retry
    }
  }

  async destroy(): Promise<void> {
    this.isDestroyed = true;
    await this.unsubscribe(); // Unsubscribe all users
    logger.info('PollingNotificationService destroyed');
  }

  // Status getters for compatibility with existing service
  get activeSubscriptions(): number {
    return this.pollingStates.size;
  }

  isSubscribed(userId: string): boolean {
    return this.pollingStates.has(userId);
  }

  getConnectionStatus(): {
    subscriptions: number;
    retrying: number;
    listeners: number;
  } {
    return {
      subscriptions: this.pollingStates.size,
      retrying: 0, // No retry mechanism in polling
      listeners: Array.from(this.listeners.values()).reduce((sum, arr) => sum + arr.length, 0)
    };
  }
}

export const pollingNotificationService = PollingNotificationService.getInstance();
export { PollingNotificationService };